/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { MutableRefObject } from 'react';

import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import Imgix from 'js/components/Imgix';
import { useParams } from 'js/lib/useRouter';

import { Button, Tag, Typography2 as Typography, breakpoints } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import CourseEnrollButton from 'bundles/enroll-common/components/CourseEnrollButton';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import { humanizeDifficultyLevel } from 'bundles/unified-description-page-common/constants';
import { useIsInUnifiedPageRollout } from 'bundles/unified-description-page-common/providers/UnifiedPageRolloutExperimentDataProvider';
import { durationToHours } from 'bundles/unified-description-page-common/utils/learningTime';

import _t from 'i18n!nls/reviews';

type Props = {
  courseId: string;
  courseName: string;
  description?: string | null;
  difficultyLevel?: DifficultyLevel | null;
  imageUrl?: string | null;
  marketingProductType?: string | null;
  partnerLogoUrl?: string | null;
  partnerName?: string | null;
  programSlug?: string;
  skillTags?: Array<string>;
  totalDuration?: string | null;
};

const MAX_SKILL_TAGS = 2;
const DESCRIPTION_MAX_LINES = 2;

const styles = {
  skillTag: css`
    span {
      font-size: 12px;
    }
  `,
  card: css`
    background: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-200);
    padding: var(--cds-spacing-200);
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(160px, 40%);
    gap: clamp(var(--cds-spacing-150), 4vw, 128px);
    align-items: stretch;

    ${breakpoints.down('sm')} {
      display: block;
      padding: var(--cds-spacing-150);
    }
  `,
  content: css`
    display: flex;
    flex-direction: column;
    min-width: 0;
  `,
  topSection: css`
    ${breakpoints.down('sm')} {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 112px;
      gap: var(--cds-spacing-150);
      align-items: start;
    }
  `,
  headingContent: css`
    min-width: 0;
  `,
  partnerLogo: css`
    height: 34px;
    width: auto;
    display: block;
    align-self: flex-start;
    object-fit: contain;
    margin-bottom: var(--cds-spacing-150);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  title: css`
    margin-bottom: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  description: css`
    color: var(--cds-color-neutral-primary-weak);
    margin-bottom: var(--cds-spacing-150);
    display: -webkit-box;
    -webkit-line-clamp: ${DESCRIPTION_MAX_LINES};
    -webkit-box-orient: vertical;
    overflow: hidden;

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  metadata: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    align-items: center;
    color: var(--cds-color-neutral-primary-weak);
    margin-bottom: var(--cds-spacing-400);

    > * {
      font-size: 12px;
    }

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  metadataDetails: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    align-items: center;

    ${breakpoints.down('sm')} {
      order: 1;
    }
  `,
  skillTags: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    align-items: center;

    ${breakpoints.down('sm')} {
      order: 2;
    }
  `,
  metaText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  enrollButton: css`
    width: 80%;
    min-width: 180px;
    margin-top: auto;

    ${breakpoints.down('sm')} {
      width: 100%;
      min-width: 0;
    }
  `,
  imageWrapper: css`
    width: 100%;
    aspect-ratio: 16 / 9;
    min-width: 0;
    border-radius: 12px;
    overflow: hidden;
    background: var(--cds-color-neutral-background-primary);
    align-self: flex-start;

    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  mobileImageWrapper: css`
    display: none;

    ${breakpoints.down('sm')} {
      display: block;
      width: 112px;
      aspect-ratio: 16 / 9;
      min-width: 112px;
      border-radius: 12px;
      overflow: hidden;
      background: var(--cds-color-neutral-background-primary);
    }
  `,
  image: css`
    width: 100%;
    height: 100%;
    display: flex;
  `,
};

const formatMarketingProductType = (marketingProductType?: string | null) => {
  if (!marketingProductType) {
    return null;
  }

  switch (marketingProductType.toUpperCase()) {
    case 'COURSE':
      return _t('Course');
    case 'GUIDED_PROJECT':
      return _t('Guided Project');
    case 'PROJECT':
      return _t('Project');
    case 'SPECIALIZATION':
      return _t('Specialization');
    case 'PROFESSIONAL_CERTIFICATE':
    case 'CERTIFICATE':
      return _t('Professional Certificate');
    case 'MASTERTRACK':
      return _t('MasterTrack Certificate');
    case 'DEGREE':
      return _t('Degree');
    default: {
      return null;
    }
  }
};

const ReviewsProductCard = ({
  courseId,
  courseName,
  description,
  difficultyLevel,
  imageUrl,
  marketingProductType,
  partnerLogoUrl,
  partnerName,
  programSlug,
  skillTags = [],
  totalDuration,
}: Props) => {
  const track = useTracker();
  const params = useParams();
  const orgSlug = params?.orgSlug;
  const isInUnifiedPageRollout = useIsInUnifiedPageRollout();
  const metadataItems: Array<{ key: string; content: React.ReactNode }> = [];
  const visibleSkillTags = skillTags.slice(0, MAX_SKILL_TAGS);
  const humanizedDifficulty = difficultyLevel ? humanizeDifficultyLevel(difficultyLevel) : null;
  const productTypeLabel = formatMarketingProductType(marketingProductType);

  const visibilityTrackingRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_section', {
    pageSection: {
      sectionName: 'page_header',
      sectionTitle: 'reviews_header',
    },
    product: {
      id: courseId,
    },
  });

  if (humanizedDifficulty) {
    metadataItems.push({
      key: 'difficulty',
      content: (
        <Typography variant="bodySecondary" component="span" css={styles.metaText}>
          {humanizedDifficulty}
        </Typography>
      ),
    });
  }

  if (productTypeLabel) {
    metadataItems.push({
      key: 'product-type',
      content: (
        <Typography variant="bodySecondary" component="span" css={styles.metaText}>
          {productTypeLabel}
        </Typography>
      ),
    });
  }

  if (totalDuration) {
    metadataItems.push({
      key: 'duration',
      content: (
        <Typography variant="bodySecondary" component="span" css={styles.metaText}>
          {durationToHours(totalDuration)}
        </Typography>
      ),
    });
  }

  return (
    <section css={styles.card} ref={visibilityTrackingRef}>
      <div css={styles.content}>
        <div css={styles.topSection}>
          <div css={styles.headingContent}>
            {partnerLogoUrl ? (
              <Imgix
                src={partnerLogoUrl}
                alt={partnerName ?? _t('Partner logo')}
                height={34}
                css={styles.partnerLogo}
              />
            ) : null}
            <Typography variant="subtitleLarge" component="h2" css={styles.title}>
              {courseName}
            </Typography>
          </div>
          {imageUrl ? (
            <div css={styles.mobileImageWrapper}>
              <div css={styles.image}>
                <LearningProductCardImage imageUrl={imageUrl} imageParams={{ height: 180, width: 320 }} />
              </div>
            </div>
          ) : null}
        </div>
        {description ? (
          <Typography variant="bodySecondary" component="p" css={styles.description}>
            {description}
          </Typography>
        ) : null}
        {metadataItems.length > 0 || visibleSkillTags.length > 0 ? (
          <div css={styles.metadata}>
            {visibleSkillTags.length ? (
              <div css={styles.skillTags}>
                {visibleSkillTags.map((tag) => (
                  <Tag key={tag} variant="status" color="highlight1" priority="secondary" css={styles.skillTag}>
                    {tag}
                  </Tag>
                ))}
              </div>
            ) : null}
            {metadataItems.length ? (
              <div css={styles.metadataDetails}>
                {metadataItems.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {index > 0 ? (
                      <Typography variant="bodySecondary" component="span" aria-hidden css={styles.metaText}>
                        ·
                      </Typography>
                    ) : null}
                    {item.content}
                  </React.Fragment>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {!programSlug && !(isInUnifiedPageRollout && orgSlug) ? (
          <CourseEnrollButton
            courseId={courseId}
            showStartDate={false}
            enrollButtonText={_t('Enroll for free')}
            renderEnrollButton={({ onClick, disabled, enrollButtonText: text, href, ariaLabel }) =>
              href ? (
                <Button
                  component="a"
                  href={href}
                  variant="primary"
                  size="small"
                  css={styles.enrollButton}
                  aria-label={ariaLabel}
                  data-e2e="enroll-button"
                >
                  {text}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="small"
                  onClick={(event) => {
                    track('click_button', {
                      button: {
                        name: 'reviews_enroll',
                      },
                      pageSection: {
                        sectionName: 'page_header',
                        sectionTitle: 'reviews_header',
                      },
                      product: {
                        id: courseId,
                      },
                    });
                    onClick?.(event);
                  }}
                  disabled={disabled}
                  css={styles.enrollButton}
                  aria-label={ariaLabel}
                  data-e2e="enroll-button"
                >
                  {text}
                </Button>
              )
            }
          />
        ) : null}
      </div>
      {imageUrl ? (
        <div css={styles.imageWrapper}>
          <div css={styles.image}>
            <LearningProductCardImage imageUrl={imageUrl} imageParams={{ height: 180, width: 320 }} />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ReviewsProductCard;
